import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { parseDate } from '../shared/dateUtils';

/**
 * DocumentAttachment - Component for displaying file attachments in messages
 * 
 * Shows image previews for images, file icons for other files, with download functionality
 */
const DocumentAttachment = ({ document, showDownload = true }) => {
  const intl = useIntl();
  const [imageError, setImageError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const isImage = (mime_type) => {
    return mime_type && mime_type.startsWith('image/') && !imageError;
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setLightboxOpen(true);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getFileIcon = (mime_type) => {
  if (!mime_type) return '📎';
  
  if (mime_type.startsWith('image/')) return '🖼️';
  if (mime_type.startsWith('video/')) return '🎥';
  if (mime_type.startsWith('audio/')) return '🎵';
  if (mime_type.includes('pdf')) return '📄';
  if (mime_type.includes('document') || mime_type.includes('word') || mime_type.includes('msword')) return '📝';
  if (mime_type.includes('spreadsheet') || mime_type.includes('excel') || mime_type.includes('sheet')) return '📊';
  if (mime_type.includes('presentation') || mime_type.includes('powerpoint') || mime_type.includes('slide')) return '📋';
  if (mime_type.includes('zip') || mime_type.includes('archive') || mime_type.includes('rar')) return '🗜️';
  if (mime_type.includes('text')) return '📃';
    
    return '📎';
  };

  const formatFileSize = (url) => {
    // Since we don't have file size in the document model, we'll skip showing it
    // In a real implementation, you might want to add a size field to the Document model
    return '';
  };

  const handleDownload = () => {
    if (document.file?.download_url) {
      window.open(document.download_url, '_blank');
    }
  };

  // Render image preview for images
  if (isImage(document.mime_type)) {
    return (
      <>
        <div style={{
          margin: '8px 0',
          maxWidth: '300px'
        }}>
          <div
            onClick={handleImageClick}
            style={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: 'var(--succession-bg-secondary)', // Changed from #f1f5f9
              border: '1px solid var(--succession-border)', // Changed from #e2e8f0
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <img
              src={document.download_url}
              alt={document.name || 'Image attachment'}
              onError={handleImageError}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '200px',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            
            {/* Image overlay with info */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              padding: '12px 16px 8px',
              color: 'var(--succession-bg-primary)' // Changed from white
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                📷 {document.name || 'Image'}
              </div>
              <div style={{
                fontSize: '11px',
                opacity: 0.9,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {document.mime_type && (
                  <span>{document.mime_type.split('/')[1]?.toUpperCase()}</span>
                )}
                {document.created_at && (
                  <>
                    <span>•</span>
                    <span>{parseDate(document.created_at).toLocaleDateString()}</span>
                  </>
                )}
                <span>• {intl.formatMessage({ id: 'succession.messaging.document_attachment.click_to_view' })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <div style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }}>
              <img
                src={document.download_url}
                alt={document.name || 'Image attachment'}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Close button */}
              <button
                onClick={() => setLightboxOpen(false)}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '0',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--succession-bg-primary)', // Changed from white
                  fontSize: '18px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ×
              </button>

              {/* Download button */}
              {showDownload && document.download_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(document.download_url, '_blank');
                  }}
                  style={{
                    position: 'absolute',
                    top: '-40px',
                    right: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--succession-bg-primary)', // Changed from white
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                >
                  ⬇
                </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Render file attachment for non-images
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      backgroundColor: 'var(--succession-bg-secondary)', // Changed from #f8fafc
      borderRadius: '12px',
      border: '1px solid var(--succession-border)', // Changed from #e2e8f0
      margin: '8px 0',
      transition: 'all 0.2s ease',
      cursor: showDownload ? 'pointer' : 'default',
      maxWidth: '300px'
    }}
    onClick={showDownload ? handleDownload : undefined}
    onMouseEnter={(e) => {
      if (showDownload) {
        e.target.style.backgroundColor = 'var(--succession-bg-primary)'; // Changed from #f1f5f9
        e.target.style.borderColor = 'var(--succession-border-hover)'; // Changed from #cbd5e1
        e.target.style.transform = 'translateY(-1px)';
        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
      }
    }}
    onMouseLeave={(e) => {
      if (showDownload) {
        e.target.style.backgroundColor = 'var(--succession-bg-secondary)'; // Changed from #f8fafc
        e.target.style.borderColor = 'var(--succession-border)'; // Changed from #e2e8f0
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = 'none';
      }
    }}>
      {/* File Icon */}
      <div style={{
        fontSize: '24px',
        flexShrink: 0
      }}>
        {getFileIcon(document.mime_type)}
      </div>

      {/* File Info */}
      <div style={{
        flex: 1,
        minWidth: 0 // Allow text to truncate
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--succession-text-primary)', // Changed from #1f2937
          marginBottom: '2px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {document.name || intl.formatMessage({ id: 'succession.messaging.document_attachment.unnamed_file' })}
        </div>
        <div style={{
          fontSize: '12px',
          color: 'var(--succession-text-muted)', // Changed from #6b7280
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {document.mime_type && (
            <span>{document.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}</span>
          )}
          {document.created_at && (
            <>
              <span>•</span>
              <span>{parseDate(document.created_at).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </div>

      {/* Download Icon */}
      {showDownload && document.download_url && (
        <div style={{
          flexShrink: 0,
          color: 'var(--succession-text-muted)', // Changed from #6b7280
          transition: 'color 0.2s ease'
        }}>
          <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      )}
    </div>
  );
};

DocumentAttachment.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
      download_url: PropTypes.string,
  mime_type: PropTypes.string,
  created_at: PropTypes.string
  }).isRequired,
  showDownload: PropTypes.bool
};

export default DocumentAttachment; 